import React, { useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { stats, values, timeline, offices, recognitions, aboutHighlights } from '../data/aboutData';
import ScrollToTop from './common/ScrollToTop';

const About = () => {
  // Memoize data for performance
  const memoizedStats = useMemo(() => stats, []);
  const memoizedValues = useMemo(() => values, []);
  const memoizedTimeline = useMemo(() => timeline, []);
  const memoizedOffices = useMemo(() => offices, []);
  const memoizedRecognitions = useMemo(() => recognitions, []);
  const memoizedHighlights = useMemo(() => aboutHighlights, []);

  // Scroll progress indicator effect with animations
  useEffect(() => {
    const handleScroll = () => {
      const scrollProgress = document.getElementById('about-scroll-progress');
      if (scrollProgress) {
        const windowHeight = window.innerHeight;
        const documentHeight = document.documentElement.scrollHeight - windowHeight;
        const scrolled = window.scrollY;
        const progress = (scrolled / documentHeight) * 100;
        scrollProgress.style.width = `${Math.min(progress, 100)}%`;
        scrollProgress.setAttribute('aria-valuenow', Math.min(progress, 100).toFixed(0));
      }

      // Intersection Observer for animations
      const observerOptions = {
        threshold: 0.1,
        rootMargin: '0px 0px -100px 0px'
      };

      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-fade-in-up');
            entry.target.style.opacity = '1';
          }
        });
      }, observerOptions);

      // Observe elements
      const animatedElements = document.querySelectorAll('.animate-on-scroll');
      animatedElements.forEach((el) => observer.observe(el));

      return () => {
        animatedElements.forEach((el) => observer.unobserve(el));
      };
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Initial check
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <>
      {/* Scroll Progress Indicator */}
      <div className="fixed top-0 left-0 w-full h-1 bg-gray-200 z-50" role="progressbar" aria-label="Page scroll progress">
        <div className="h-full bg-gradient-to-r from-orange-500 to-pink-500 transition-all duration-300 ease-out" style={{width: '0%'}} id="about-scroll-progress" aria-valuenow={0} aria-valuemin={0} aria-valuemax={100}></div>
      </div>
      <Helmet>
        <title>About Us - Sri Lanka's No.1 Study Abroad Consultant | Ravana Migration</title>
        <meta name="description" content="Leading study abroad consultant for Germany, Canada, Australia, UK, and New Zealand. 99% success rate, 1000+ students assisted, global presence with offices in 3 countries." />
      </Helmet>

      <div className="min-h-screen bg-white">
        {/* Hero Section */}
        <div className="relative overflow-hidden bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 py-16 sm:py-20 lg:py-24">
          {/* Animated background elements */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute -top-40 -right-40 w-80 h-80 bg-blue-400 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-float"></div>
            <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-purple-400 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-float" style={{animationDelay: '2s'}}></div>
          </div>

          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
            <div className="text-center animate-fade-in-up" style={{opacity: 0}}>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
                <span className="relative inline-block">
                  <span className="relative z-10">About Ravana Migration</span>
                  <svg className="absolute -bottom-2 left-0 w-full h-6 text-blue-400 opacity-30" viewBox="0 0 200 20" fill="currentColor">
                    <path d="M0 15 Q 50 5 100 15 T 200 15 L 200 20 L 0 20 Z" />
                  </svg>
                </span>
              </h1>
              <div className="rule mx-auto mb-6"></div>
              <p className="text-xl sm:text-2xl text-gray-700 mb-6 max-w-3xl mx-auto font-medium">
                Sri Lanka's No.1 Study Abroad Consultant
              </p>
              <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
                Empowering students to achieve their international education dreams across five countries with transparency, expertise, and dedication.
              </p>

              {/* Quick stats */}
              <div className="flex flex-wrap justify-center gap-6 sm:gap-8 text-sm text-gray-600 mt-10">
                <div className="flex items-center gap-2 bg-white/60 px-4 py-2 rounded-full backdrop-blur-sm">
                  <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z" />
                  </svg>
                  <span className="font-semibold">1000+ Students</span>
                </div>
                <div className="flex items-center gap-2 bg-white/60 px-4 py-2 rounded-full backdrop-blur-sm">
                  <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span className="font-semibold">99% Success Rate</span>
                </div>
                <div className="flex items-center gap-2 bg-white/60 px-4 py-2 rounded-full backdrop-blur-sm">
                  <svg className="w-5 h-5 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z" />
                  </svg>
                  <span className="font-semibold">5 Countries</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* About Highlights */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
          <div className="text-center mb-12 animate-on-scroll" style={{opacity: 0}}>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">Our Impact</h2>
            <div className="rule mx-auto mb-4"></div>
            <p className="text-lg sm:text-xl text-gray-600">Numbers that speak for themselves</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 animate-on-scroll" style={{opacity: 0}} role="list" aria-label="About highlights">
            {memoizedHighlights.map((highlight, index) => (
              <div key={index} className="card text-center p-6 hover-lift group" role="listitem">
                <div className="text-5xl mb-4 group-hover:scale-110 transition-transform" aria-hidden="true">{highlight.icon}</div>
                <div className="text-2xl md:text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600 mb-2 group-hover:scale-105 transition-transform">
                  {highlight.value}
                </div>
                <div className="text-sm font-semibold text-gray-900 mb-2">{highlight.title}</div>
                <div className="text-xs text-gray-600">{highlight.description}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Stats Section */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 animate-on-scroll" style={{opacity: 0}} role="list" aria-label="Company statistics">
            {memoizedStats.map((stat, index) => (
              <div key={index} className="card text-center card-hover group hover-lift" role="listitem">
                <div className="text-5xl mb-4 group-hover:scale-110 transition-transform" aria-hidden="true">{stat.icon}</div>
                <div className="text-3xl md:text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600 mb-2 group-hover:scale-105 transition-transform">{stat.number}</div>
                <p className="text-sm text-gray-600 font-medium">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Our Story */}
        <div className="bg-gray-50 py-16 sm:py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-12 animate-on-scroll" style={{opacity: 0}}>
                <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">Our Story</h2>
                <div className="rule mx-auto"></div>
              </div>

              <div className="card bg-white space-y-6 text-gray-700 leading-relaxed shadow-lg hover-lift animate-on-scroll" style={{opacity: 0}}>
                <p className="text-lg">
                  Ravana Migration, a specialized division of Ravana Industries Pvt Ltd, was founded with a simple yet powerful vision: to make quality international education accessible to every deserving student.
                </p>

                <p>
                  What started as a focused effort to help students study in Germany has evolved into <strong>Flight Plan™ Alliance Partner</strong> — a comprehensive migration platform supporting students across five countries: Germany, Canada, Australia, the United Kingdom, and New Zealand.
                </p>

                <p>
                  <strong>Flight Plan™</strong> is an alliance that provides standardised migration services in Sri Lanka that is AI powered. Ravana Migration is a proud alliance partner.
                </p>

                <p>
                  With dedicated offices in Sri Lanka, Germany, and Canada, we provide not just consultation but complete end-to-end support. Our team includes immigration lawyers, education consultants, and country experts who have firsthand experience living and working in your destination country.
                </p>

                <p>
                  We believe in transparency above all else. Every service is documented in a signed contract, every payment goes through bank transfer with proper invoices, and every promise we make is backed by our <strong>99% success rate</strong> for qualified applicants.
                </p>

                <p className="text-lg font-semibold text-gray-900">
                  Today, we're proud to have assisted over 1000 students in achieving their dreams of studying abroad, and we continue to set new standards in the migration consulting industry.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Our Values */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
          <div className="text-center mb-12 animate-on-scroll" style={{opacity: 0}}>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">Our Core Values</h2>
            <div className="rule mx-auto"></div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 animate-on-scroll" style={{opacity: 0}} role="list" aria-label="Company values">
            {memoizedValues.map((value, index) => (
              <div key={index} className="card card-hover text-center group hover-lift" role="listitem">
                <div className="text-6xl mb-4 group-hover:scale-110 transition-transform" aria-hidden="true">{value.icon}</div>
                <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-blue-600 transition-colors">{value.title}</h3>
                <p className="text-sm text-gray-600">{value.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Timeline */}
        <div className="relative overflow-hidden bg-gradient-to-br from-purple-50 via-blue-50 to-indigo-50 py-16 sm:py-20">
          {/* Animated background elements */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute -top-40 -right-40 w-80 h-80 bg-purple-400 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-float"></div>
            <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-blue-400 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-float" style={{animationDelay: '2s'}}></div>
          </div>

          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
            <div className="text-center mb-12 animate-on-scroll" style={{opacity: 0}}>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">Our Journey</h2>
              <div className="rule mx-auto"></div>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 animate-on-scroll" style={{opacity: 0}} role="list" aria-label="Company timeline">
              {memoizedTimeline.map((item, index) => (
                <div key={index} className="card bg-white text-center card-hover group hover-lift relative overflow-hidden" role="listitem">
                  {/* Gradient overlay on hover */}
                  <div className="absolute inset-0 bg-gradient-to-br from-purple-50/0 to-blue-50/0 group-hover:from-purple-50/30 group-hover:to-blue-50/30 transition-all duration-300"></div>

                  <div className="relative">
                    <div className="text-3xl md:text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-blue-600 mb-3 group-hover:scale-105 transition-transform">{item.year}</div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-purple-600 transition-colors">{item.title}</h3>
                    <p className="text-sm text-gray-600">{item.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Global Presence */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
          <div className="text-center mb-12 animate-on-scroll" style={{opacity: 0}}>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">Global Presence</h2>
            <div className="rule mx-auto"></div>
          </div>

          <div className="grid md:grid-cols-3 gap-6 animate-on-scroll" style={{opacity: 0}} role="list" aria-label="Office locations">
            {memoizedOffices.map((office, index) => (
              <div key={index} className="card card-hover group hover-lift relative overflow-hidden" role="listitem">
                {/* Gradient overlay on hover */}
                <div className="absolute inset-0 bg-gradient-to-br from-blue-50/0 to-purple-50/0 group-hover:from-blue-50/30 group-hover:to-purple-50/30 transition-all duration-300"></div>

                <div className="relative">
                  <div className="text-center mb-6">
                    <span className="text-7xl sm:text-8xl transform group-hover:scale-110 transition-transform inline-block" aria-hidden="true">{office.flag}</span>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2 text-center group-hover:text-blue-600 transition-colors">{office.country}</h3>
                  <p className="text-sm text-blue-600 text-center mb-4 font-semibold">{office.role}</p>
                  <div className="space-y-3 text-sm text-gray-700">
                    <p className="flex items-start gap-2">
                      <svg className="w-5 h-5 text-gray-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                      </svg>
                      <span><strong>City:</strong> {office.city}</span>
                    </p>
                    <p className="flex items-start gap-2">
                      <svg className="w-5 h-5 text-gray-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                      </svg>
                      <span><strong>Address:</strong> {office.address}</span>
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Recognitions */}
        <div className="bg-gray-50 py-16 sm:py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12 animate-on-scroll" style={{opacity: 0}}>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">Recognitions & Achievements</h2>
              <div className="rule mx-auto"></div>
            </div>

            <div className="flex flex-wrap justify-center gap-6 animate-on-scroll" style={{opacity: 0}} role="list" aria-label="Company recognitions">
              {memoizedRecognitions.map((recognition, index) => (
                <div key={index} className="flex items-center gap-3 card card-hover px-6 py-4 group hover-lift" role="listitem">
                  <span className="text-4xl group-hover:scale-110 transition-transform" aria-hidden="true">{recognition.icon}</span>
                  <span className="font-semibold text-gray-900 group-hover:text-blue-600 transition-colors">{recognition.text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
          <div className="relative overflow-hidden rounded-3xl shadow-2xl animate-on-scroll" style={{opacity: 0}}>
            {/* Animated background */}
            <div className="absolute inset-0 bg-gradient-to-br from-blue-600 via-purple-600 to-indigo-700"></div>
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
              <div className="absolute -top-40 -right-40 w-80 h-80 bg-white opacity-10 rounded-full mix-blend-overlay filter blur-xl animate-float"></div>
              <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-white opacity-10 rounded-full mix-blend-overlay filter blur-xl animate-float" style={{animationDelay: '2s'}}></div>
            </div>

            <div className="relative text-center py-16 px-4 sm:py-20 sm:px-6 lg:px-8">
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6 leading-tight">
                Ready to Start Your Journey?
              </h2>
              <div className="rule mx-auto mb-6 bg-white/30"></div>
              <p className="text-lg sm:text-xl text-white mb-10 max-w-3xl mx-auto leading-relaxed">
                Join over 1000 students who trusted us with their dreams
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/choice" className="btn bg-white text-blue-600 hover:bg-gray-100 text-lg px-8 py-4 shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all duration-300 group">
                  <span>Start Free Assessment</span>
                  <svg className="w-5 h-5 ml-2 transition-transform group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                </Link>
                <Link to="/contact" className="btn btn-outline text-white border-white/80 hover:bg-white hover:text-blue-600 text-lg px-8 py-4 backdrop-blur-sm transform hover:scale-105 transition-all duration-300 group">
                  <svg className="w-5 h-5 mr-2 transition-transform group-hover:scale-110" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  <span>Contact Us</span>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Floating Mobile CTA */}
      <div className="fixed bottom-4 left-4 right-24 z-50 md:hidden">
        <Link
          to="/choice"
          className="btn btn-accent w-full text-center text-lg py-4 shadow-2xl animate-pulse-slow"
          aria-label="Start your free assessment"
        >
          <svg className="w-5 h-5 inline-block mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          Get Started →
        </Link>
      </div>

      {/* Scroll to Top */}
      <ScrollToTop />
    </>
  );
};

export default About;
